/**
 * NOTE: For compatibility across platforms all calls **must** return a
 * `Promise` object!
 */

import * as Platform from '../Platform';
import { PlayerAbstract } from '../PlayerAbstract';
import { IframeMessengerMixin } from '../mixins/IframeMessengerMixin';
import { BrowserReloadable } from '../mixins/BrowserReloadable';
import { Log } from '../../AuxillaryService/Log';
import { Deferred } from '../../Utility/Deferred';
import _ from 'underscore';
import { isUrlQueryPlatform } from '../PlatformCheck';

/**
 * Iframe messenger request
 * @typedef {object} IframeMessengerRequest
 * @memberof WebOS.
 * @property {string} action
 * @property {number} id - unique identifier for request promise
 * @property {Object.<string, string|number>} options - other options
 */

/**
 * Iframe messenger response
 * @typedef {object} IframeMessengerResponse
 * @memberof WebOS.
 * @property {string} origin - Iframe Origin
 * @property {object} source - Has information about request source
 * @property {Object.<string, string|number>} data - Response data
 */

/**
 * WebOS Player
 * @constructor
 * @augments PlayerAbstract
 * @implements NamedValueCacheInterface
 * @implements ApplicationStateInterface
 * @implements RestartApplicationInterface
 * @implements ScreenshotInterface
 * @implements ReloadInterface
 * @implements LogInterface
 * @implements SystemInformationInterface
 * @mixes IframeMessengerMixin
 * @mixes BrowserReloadable
 */
function WebOSPlayer() {
    PlayerAbstract.call(this);

    //To initialize Porthole because WebOSPlayer runs signs in an iframe
    IframeMessengerMixin.call(this, './proxy.html');
    this.addIframeEventListener(parentIframeMessageHandler.bind(this));

    /**
     * Array of request promises
     * @member {Array} WebOSPlayer#promises
     */
    Object.defineProperty(this, 'promises', {
        value: [],
        writable: false,
        configurable: false,
        enumerable: false,
    });
}

WebOSPlayer.prototype = Object.create(BrowserReloadable(PlayerAbstract).prototype);
WebOSPlayer.prototype.constructor = WebOSPlayer;

/**
 * @inheritDoc
 */
WebOSPlayer.prototype.type = Platform.WEBOS;

/**
 * @inheritDoc
 */
WebOSPlayer.prototype.name = 'WebOS';

/**
 * @inheritDoc
 */
WebOSPlayer.prototype.getHostPlatform = function () {
    return Promise.resolve(this.name);
};

/**
 * Gets network status
 * @returns {Deferred}
 */
WebOSPlayer.prototype.getNetworkStatus = function () {
    return invokePlayerMethod.call(this, 'GET_NETWORK_STATUS');
};

/**
 * @typedef {Object} DeviceInformation
 * @memberof WebOS.
 * @property {string} vendor
 * @property {string} browserPlatform
 * @property {string} browserName
 * @property {string} browserVersion
 * @property {string} model
 * @property {string} platform
 * @property {string} osName
 * @property {string} osVersion
 * @property {string} uniqueIdentifier - This is immutable. This is serial
 * number of device.
 */

/**
 * Gets Device information
 * @async
 * @returns {Deferred<WebOS.DeviceInformation>}
 */
WebOSPlayer.prototype.getDeviceInformation = function () {
    return invokePlayerMethod.call(this, 'GET_DEVICE_INFORMATION');
};

/**
 * Returns a device-specific hardware ID based on WebOS info.
 *
 * @returns {Promise<string>}
 */
WebOSPlayer.prototype._generateAndGetHardwareId = function () {
    return this.getDeviceInformation().then(function (deviceInfo) {
        return (
            'WebOS://' +
            deviceInfo.platform +
            ':' +
            deviceInfo.model +
            ':' +
            deviceInfo.uniqueIdentifier
        );
    });
};

/**
 * @inheritDoc
 */
WebOSPlayer.prototype.getNamedValue = function (keyName, defaultValue) {
    return invokePlayerMethod.call(this, 'GET_NAMED_VALUE', {
        key: keyName,
        value: defaultValue,
    });
};

/**
 * @inheritDoc
 */
WebOSPlayer.prototype.setNamedValue = function (keyName, keyValue) {
    return invokePlayerMethod.call(this, 'SET_NAMED_VALUE', {
        key: keyName,
        value: keyValue,
    });
};

/**
 * @inheritDoc
 */
WebOSPlayer.prototype.deleteNamedValue = function (keyName) {
    return invokePlayerMethod.call(this, 'DELETE_NAMED_VALUE', {
        key: keyName,
    });
};

/**
 * @inheritDoc
 */
WebOSPlayer.prototype.KeyExists = function (keyName) {
    return invokePlayerMethod.call(this, 'CONFIG_KEY_EXISTS', {
        key: keyName,
    });
};

/**
 * @inheritDoc
 */
WebOSPlayer.prototype.getPlayerVersion = function () {
    return invokePlayerMethod.call(this, 'GET_PLAYER_VERSION');
};

/**
 * @inheritDoc
 */
WebOSPlayer.prototype.getScreenshot = function () {
    return invokePlayerMethod.call(this, 'GET_SCREENSHOT');
};

/**
 * @inheritDoc
 */
WebOSPlayer.prototype.getPlatformLogs = function () {
    return getData.call(this, 'PLATFORM_LOGS');
};

/**
 * Set log level on the player
 * @param {string} logLevel - info, warn, error, debug
 * @returns {Deferred}
 */
WebOSPlayer.prototype.setLogLevel = function (logLevel) {
    var setLogAction = {
        info: 'SET_LOG_LEVEL_INFO',
        warn: 'SET_LOG_LEVEL_WARN',
        error: 'SET_LOG_LEVEL_ERROR',
        debug: 'SET_LOG_LEVEL_DEBUG',
    };
    if (!setLogAction[logLevel]) {
        return Deferred.rejected(new Error('Invalid log level.'));
    } else {
        return performPlayerAction.call(this, setLogAction[logLevel]);
    }
};

/**
 * Enables file logging on player
 * @returns {Deferred}
 */
WebOSPlayer.prototype.enablePersistentLog = function () {
    return performPlayerAction.call(this, 'ENABLE_PERSISTENT_LOG');
};

/**
 * Disables file logging in player
 * @returns {Deferred}
 */
WebOSPlayer.prototype.disablePersistentLog = function () {
    return performPlayerAction.call(this, 'DISABLE_PERSISTENT_LOG');
};

/**
 * @typedef ApplicationState
 * @memberof WebOS.
 * @property {string} appState
 * @property {string} playerUuid
 * @property {string} deviceId
 * @property {boolean} isOnline
 * @property {string} logLevel
 * @property {boolean} persistLogs
 * @property {string} orientation
 * @property {boolean} startOnBoot
 * @property {boolean} startOnIdle
 * @property {boolean} wakeLockAcquired
 * @property {boolean} sleepOnNoContent
 * @property {object[]} streamSchedules
 * @property {number} volume
 * @property {boolean} muted
 * @property {string} build
 * @property {WebOS.DeviceInformation} device
 */

/**
 * Gets current application state
 * @returns {Deferred<WebOS.ApplicationState>}
 */
WebOSPlayer.prototype.getApplicationState = function () {
    return getData.call(this, 'APPLICATION_STATE');
};

/**
 * Gets System information - Hardware and OS
 * Pascal case for compatibility
 * @returns {Promise}
 */
WebOSPlayer.prototype.getSystemInformation = function () {
    return getData.call(this, 'SYSTEM_INFORMATION').then(function (data) {
        return {
            HardwareInfo: {
                Manufacturer: data.hardware.manufacturer,
                Model: data.hardware.model,
            },
            OperatingSystemInfo: {
                Name: data.os.name,
                Manufacturer: data.os.manufacturer,
                Version: data.os.version,
            },
        };
    });
};

/**
 * Does a cold restart of the application
 * @returns {Promise}
 */
WebOSPlayer.prototype.restartApplication = function () {
    this.postMessageToParentIframe({
        action: 'APPLICATION_RESTART',
    });

    return Promise.resolve();
};

/**
 * Set no scheduled content state in WebOS device
 * @returns {Deferred}
 */
WebOSPlayer.prototype.setNoScheduledContent = function () {
    return performPlayerAction.call(this, 'NO_SCHEDULED_CONTENT');
};

/**
 * Checks if the device is running WebOS on
 * @returns {boolean}
 */
WebOSPlayer.isTheRunningPlatform = function () {
    const thisPlatformIsRunning = isUrlQueryPlatform('WebOS');
    if (typeof thisPlatformIsRunning === 'boolean') {
        return thisPlatformIsRunning;
    }

    // use fallback check
    var userAgent = navigator.userAgent;

    var validStrings = ['WebOS', 'Web0S'];

    var index = _.findIndex(validStrings, function (name) {
        return userAgent.indexOf(name) !== -1;
    });

    return index !== -1;
};

/** Aliases for compatibility **/

/**
 * @inheritDoc
 */
WebOSPlayer.prototype.GetValueOrDefault = WebOSPlayer.prototype.getNamedValue;

/**
 * @inheritDoc
 */
WebOSPlayer.prototype.GetValue = WebOSPlayer.prototype.getNamedValue;

/**
 * @inheritDoc
 */
WebOSPlayer.prototype.SetValue = WebOSPlayer.prototype.setNamedValue;

/**
 * @inheritDoc
 */
WebOSPlayer.prototype.DeleteValue = WebOSPlayer.prototype.deleteNamedValue;

/**
 * Stores last used message id
 * @type {number}
 */
WebOSPlayer.messageIdCounter = 0;

/**
 * Store boot time to prevent collision with message id in the platform layer
 * Collision is possible if the signs layer refreshes but
 * the platform does not refresh and persists the same message id list
 * @type {number}
 */
WebOSPlayer.bootTime = Date.now().toString().padStart(14, '0');

/**
 * Generates unique id that is used to identify request/response messages,
 * so that multiple requests can be sent
 * @returns {number}
 * @private
 */
function getMessageId() {
    return parseInt((++WebOSPlayer.messageIdCounter).toString() + WebOSPlayer.bootTime);
}

/**
 * Handler for any message coming from parent Iframe
 * @param {WebOS.IframeMessengerResponse} event - Response
 * event
 * @returns {void}
 * @throws Error
 * @private
 */
function parentIframeMessageHandler(event) {
    var message = event.data;

    /**
     * If data property is not present the message is invalid or
     * the origin is not valid
     */
    if (!message) {
        return;
    }
    var promiseIndex = _.findLastIndex(this.promises, { id: message.id });
    if (promiseIndex === -1) {
        throw new Error('Cannot resolve. Promise not found for ' + message.action);
    }

    Log.Debug('Message received from parent frame: ', null, __fileline, message);

    if (!message.action) {
        throw new Error("'action' should be present in the message.");
    }

    switch (message.action) {
        case 'GET_SCREENSHOT':
            this.promises[promiseIndex].promise.resolve({
                //remove the data type part because CMS adds it
                base64Image: message.data.replace('data:image/jpeg;base64,', ''),
            });
            break;
        case 'GET_PLAYER_VERSION':
        case 'GET_NAMED_VALUE':
        case 'SET_NAMED_VALUE':
        case 'DELETE_NAMED_VALUE':
        case 'CONFIG_KEY_EXISTS':
        case 'GET_NETWORK_STATUS':
        case 'GET_DEVICE_INFORMATION':
        case 'GET_DATA':
        case 'PLAYER_ACTION':
        case 'GET_LOGS':
            this.promises[promiseIndex].promise.resolve(message.data);
            break;
        default:
            throw new Error("'action' not recognized.");
    }

    this.promises.splice(promiseIndex, 1);
}

/**
 * Gets data from player
 * @param {string} type - type of data
 * @returns {Deferred}
 * @private
 */
function getData(type) {
    return invokePlayerMethod.call(this, 'GET_DATA', { type: type });
}

/**
 * Performs a player action
 * @param {string} type
 * @returns {Deferred}
 * @private
 */
function performPlayerAction(type) {
    return invokePlayerMethod.call(this, 'PLAYER_ACTION', { type: type });
}

/**
 * Creates request promise that sends message to platform (WebOS) layer and
 * pushes it to promises array. The promise is resolved once the platform layer
 * sends a message back
 * @see More information on how the message is processed
 * {@link https://bitbucket.org/reachmedianetwork/signs-unified/src/master/src/services/signs/SignsIframeMessengerService.ts}
 * @param {string} action
 * @param {Object.<string, string|number>} options - miscellaneous options for
 * request
 * @returns {Deferred}
 * @private
 */
function invokePlayerMethod(action, options) {
    var getDataPromise = new Deferred();
    var id = getMessageId.call(this);
    this.postMessageToParentIframe(
        Object.assign(
            {
                action: action,
                id: id,
            },
            options
        )
    );

    this.promises.push({
        id: id,
        promise: getDataPromise,
    });

    return getDataPromise;
}

export { WebOSPlayer };
